import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type CollectionAdminItem,
  type CreateCollectionInput,
  type PaginatedResponse,
  LOCALES,
  type Locale,
  hasNextPage,
} from '@bailafm/shared';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PaginationControls } from '@/components/PaginationControls';
import { CollectionDeleteDialog } from '@/components/collections/CollectionDeleteDialog';
import { CollectionFormDialog } from '@/components/collections/CollectionFormDialog';
import { CopyUrlButton } from '@/components/collections/CopyUrlButton';
import { ApiClientError, collectionsApi } from '@/lib/api';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;

export default function CollectionsPage() {
  const [pageData, setPageData] = useState<PaginatedResponse<CollectionAdminItem> | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CollectionAdminItem | null>(null);
  const [toDelete, setToDelete] = useState<CollectionAdminItem | null>(null);

  // Descarta respuestas fuera de orden: si dos llamadas a refresh() están
  // en vuelo (p. ej. doble clic en paginación), solo aplica el resultado
  // de la más reciente.
  const latestRequestRef = useRef(0);

  const refresh = useCallback(async (p: number) => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await collectionsApi.list(p, PAGE_SIZE);
      if (requestId !== latestRequestRef.current) return;
      setPageData(data);
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;
      setLoadError(
        err instanceof ApiClientError
          ? err.message
          : 'No se pudieron cargar las colecciones.',
      );
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [setPageData, setLoading, setLoadError]);

  useEffect(() => {
    void refresh(page);
  }, [page, refresh]);

  const totalPages = pageData ? Math.ceil(pageData.total / PAGE_SIZE) : 0;

  async function handleCreateOrUpdate(
    values: CreateCollectionInput,
    collection?: CollectionAdminItem,
  ): Promise<CollectionAdminItem> {
    // El refresh lo dispara onSaved, una vez completado el guardado +
    // la subida de imágenes (si las hay); hacerlo aquí también duplica
    // el fetch y encima con datos aún no definitivos.
    if (collection) {
      return collectionsApi.update(collection.id, values);
    }
    return collectionsApi.create(values);
  }

  async function handleDelete(collection: CollectionAdminItem) {
    await collectionsApi.delete(collection.id, collection._etag ?? '');
    const wasLastItemOnPage = pageData?.items.length === 1 && pageData.page > 1;
    setPageData((prev) => {
      if (!prev) return prev;
      const items = prev.items.filter((c) => c.id !== collection.id);
      const total = prev.total - 1;
      return { ...prev, items, total, hasNextPage: hasNextPage(prev.page, prev.pageSize, total) };
    });
    if (wasLastItemOnPage) {
      setPage((p) => p - 1);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Colecciones</h1>
          <p className="text-muted-foreground">
            Agrupaciones temáticas de eventos. Cada colección tiene un nombre
            trilingüe y un orden manual de eventos.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="size-4" aria-hidden /> Crear colección
        </Button>
      </header>

      {loadError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loadError}
        </p>
      )}

      {loading && (
        <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
          Cargando…
        </p>
      )}

      {!loading && pageData && pageData.items.length === 0 && !loadError && (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
          Todavía no hay colecciones. Crea la primera para empezar.
        </p>
      )}

      {!loading && pageData && pageData.items.length > 0 && (
        <>
          <ul className="space-y-2">
            {pageData.items.map((collection) => (
              <li key={collection.id}>
                <CollectionRow
                  collection={collection}
                  onEdit={() => {
                    setEditing(collection);
                    setFormOpen(true);
                  }}
                  onDelete={() => setToDelete(collection)}
                />
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}

      <CollectionFormDialog
        open={formOpen}
        collection={editing ?? undefined}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
        onSubmit={(values, collection) => handleCreateOrUpdate(values, collection)}
        onSaved={() => void refresh(page)}
      />

      <CollectionDeleteDialog
        collection={toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

interface CollectionRowProps {
  collection: CollectionAdminItem;
  onEdit(): void;
  onDelete(): void;
}

function CollectionRow({ collection, onEdit, onDelete }: CollectionRowProps) {
  const preview =
    collection.label.es ??
    collection.label.ca ??
    collection.label.en ??
    '(sin traducción)';

  return (
    <article className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{preview}</span>
          <LocalesBadges label={collection.label} />
          {collection.complete && (
            <span
              className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700"
              title="Las tres traducciones están completas"
            >
              <Check className="size-3" aria-hidden /> ok
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>
            {collection.events.length}{' '}
            {collection.events.length === 1 ? 'evento' : 'eventos'}
          </span>
          <span aria-hidden>·</span>
          <span>{formatDateRange(collection.dateFrom, collection.dateTo)}</span>
          <span aria-hidden>·</span>
          <span>{formatMinPrice(collection.minPrice)}</span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <CopyUrlButton slug={collection.slug} label={collection.label} />
        <Button type="button" variant="ghost" size="icon" aria-label="Editar" onClick={onEdit}>
          <Pencil className="size-4" aria-hidden />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Eliminar"
          onClick={onDelete}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </div>
    </article>
  );
}

function LocalesBadges({ label }: { label: CollectionAdminItem['label'] }) {
  return (
    <span className="flex items-center gap-1">
      {LOCALES.map((l) => {
        const present = typeof label[l as Locale] === 'string';
        return (
          <span
            key={l}
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              present
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground/60',
            )}
            aria-label={`${l} ${present ? 'presente' : 'ausente'}`}
          >
            {l}
          </span>
        );
      })}
    </span>
  );
}

function formatDateRange(from: string | null, to: string | null): string {
  if (!from && !to) return 'sin fechas';
  const f = from ? formatDate(from) : '?';
  const t = to ? formatDate(to) : '?';
  if (f === t) return f;
  return `${f} → ${t}`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatMinPrice(price: number | null): string {
  if (price === null) return 'sin precio';
  return `desde ${new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(price)}`;
}
